import { Link } from 'react-router-dom';
import { reportRequestId } from '../../lib/reportRequestId.js';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SenseReviewCase,
  SenseReviewDecision,
  SenseFixField,
  SenseFixPatch,
} from '@orborix/shared-types';
import {
  fetchSenseReviews,
  fetchSenseReview,
  decideSenseReview,
} from '../../api/admin/lexicon-reports.js';
import './reports.css';
const reasons: Record<string, string> = {
  wrong: 'Está mal',
  inappropriate: 'Es impropio',
  missing: 'Falta un significado',
};
const fields: Record<string, string> = {
  translation: 'Traducción',
  alts: 'Otras traducciones',
  definition: 'Definición',
  example: 'Ejemplo',
  example_es: 'Traducción del ejemplo',
  quitar: 'Retirar significado',
  agregar: 'Agregar significado',
};
const actions: Record<string, string> = {
  keep: 'Mantener la ficha',
  correct: 'Corregir el significado',
  add: 'Agregar un significado',
  retire: 'Retirar el significado',
  escalate: 'Necesita revisión',
  unavailable: 'No hay una sugerencia válida',
};
const states: Record<string, string> = {
  received: 'Recibido',
  reviewing: 'Preparando sugerencia',
  paused: 'En pausa',
  needs_review: 'Necesita revisión',
  unchanged: 'Sugiere mantener',
  approved_pending: 'Sugerencia lista',
  aceptada: 'Aceptado · espera la pasada',
  publicada: 'Publicado',
  desactualizada: 'La ficha cambió; requiere revisión',
  ambigua: 'Más de una coincidencia; requiere revisión',
  revertida: 'Publicación revertida',
};
const errorTypes = {
  missed: 'Dejó pasar un error',
  bad_fix: 'Propuso una corrección incorrecta',
  unsuitable: 'Contenido impropio',
  missing: 'Significado faltante',
  evidence: 'Evidencia insuficiente',
  other: 'Otro error',
};
function TextValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <p className="muted">Sin contenido</p>;
  if (Array.isArray(value))
    return (
      <>
        {value.map((v, i) => (
          <div key={i}>
            <TextValue value={v} />
          </div>
        ))}
      </>
    );
  if (typeof value === 'object')
    return (
      <dl>
        {Object.entries(value)
          .filter(
            ([k]) => k !== 'id' && k !== 'ipa' && k !== 'cefr' && k !== 'phrasal' && k !== 'word',
          )
          .map(([k, v]) => (
            <div key={k}>
              <dt>
                {fields[k] ??
                  (
                    {
                      exampleEs: 'Traducción del ejemplo',
                      pos: 'Categoría',
                      meanings: 'Significados',
                    } as Record<string, string>
                  )[k] ??
                  k}
              </dt>
              <dd>
                <TextValue value={v} />
              </dd>
            </div>
          ))}
      </dl>
    );
  return <p>{String(value)}</p>;
}
function Compare({ patch }: { patch: SenseFixPatch }) {
  return (
    <section className="sense-compare">
      <h3>{fields[patch.field]}</h3>
      <div className="sense-columns">
        <div>
          <h4>Antes</h4>
          <TextValue value={patch.before} />
        </div>
        <div>
          <h4>Después</h4>
          {patch.field === 'quitar' ? (
            <p>Este significado se retirará para todos en la pasada mensual.</p>
          ) : (
            <TextValue value={patch.after} />
          )}
        </div>
      </div>
    </section>
  );
}
export function DictionaryReportsPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['sense-reviews'], queryFn: fetchSenseReviews, retry: false });
  return (
    <main className="sense-reports">
      <nav className="sense-mobile-nav" aria-label="Diccionario">
        <Link to="/dictionary/reports">Avisos</Link>
        <Link to="/dictionary/missing">Faltantes</Link>
      </nav>
      <h1>Avisos de significados</h1>
      <p className="muted">
        Revisá cada sugerencia. Lo aceptado espera la pasada mensual del diccionario.
      </p>
      {list.isError && <p role="alert">{list.error.message}</p>}
      {list.data && (
        <>
          <section className="card sense-stats">
            <h2>Coincidencias con tus decisiones</h2>
            {(['real', 'simulated'] as const).map((kind) => {
              const s = list.data.data.stats[kind];
              return (
                <div key={kind}>
                  <h3>{kind === 'real' ? 'Avisos reales' : 'Banco con sugerencias simuladas'}</h3>
                  <p>
                    {s.agree} coincidencias · {s.wrong} errores · {s.editorial} ajustes humanos ·{' '}
                    {s.unavailable} sin sugerencia válida
                  </p>
                  {Object.entries(s.errors).map(([type, n]) => (
                    <p key={type}>
                      {errorTypes[type as keyof typeof errorTypes]}: {n}
                    </p>
                  ))}
                </div>
              );
            })}
            <p className="muted">
              Se cuenta la última decisión por caso. Los casos sin sugerencia y ajustes humanos se
              muestran aparte.
            </p>
          </section>
          <div className="sense-workspace">
            <section className="card sense-list" aria-label="Casos">
              {list.data.data.cases.map((c) => (
                <button
                  className={`sense-case ${selected === c.id ? 'selected' : ''}`}
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                  aria-pressed={selected === c.id}
                >
                  <b>{c.word}</b>
                  <span>{reasons[c.reason]}</span>
                  <span>
                    {c.humanAction
                      ? ({ accept: 'Aceptado', reject: 'Rechazado', manual: 'Corregido a mano' }[
                          c.humanAction
                        ] ?? c.humanAction)
                      : states[c.status]}
                  </span>
                  <time dateTime={c.createdAt}>
                    {new Date(c.createdAt).toLocaleString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires',
                    })}
                  </time>
                </button>
              ))}
            </section>
            {selected ? (
              <CaseDetail key={selected} id={selected} />
            ) : (
              <p>Elegí un caso para revisar.</p>
            )}
          </div>
        </>
      )}
    </main>
  );
}
function CaseDetail({ id }: { id: string }) {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: ['sense-review', id],
    queryFn: () => fetchSenseReview(id),
    retry: false,
  });
  const [mode, setMode] = useState<'manual' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [assessment, setAssessment] = useState<SenseReviewDecision['assessment']>('wrong');
  const [errorType, setErrorType] =
    useState<NonNullable<SenseReviewDecision['errorType']>>('bad_fix');
  const [draft, setDraft] = useState<Array<{ field: SenseFixField; after: unknown }>>([]);
  const [pending, setPending] = useState<SenseReviewDecision | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const c = q.data?.data;
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (c?.id) {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [c?.id]);

  async function submit(action: SenseReviewDecision['action']) {
    if (!c) return;
    const body =
      pending ??
      ({
        requestId: reportRequestId(),
        revision: c.revision,
        suggestionHash: c.suggestionHash,
        action,
        reason: reason.trim() || (action === 'accept' ? 'Acepto esta propuesta.' : ''),
        assessment:
          action === 'accept'
            ? c.reason === 'inappropriate' && c.proposal.action !== 'retire'
              ? 'editorial'
              : 'agree'
            : !c.proposal.available
              ? 'unavailable'
              : assessment,
        ...(action !== 'accept' && c.proposal.available && assessment === 'wrong'
          ? { errorType }
          : {}),
        ...(action === 'manual' ? { patches: draft } : {}),
      } satisfies SenseReviewDecision);
    setPending(body);
    setBusy(true);
    setError('');
    try {
      await decideSenseReview(id, body);
      setPending(null);
      setMode(null);
      setSaved(true);
      heading.current?.focus({ preventScroll: true });
      await client.invalidateQueries({ queryKey: ['sense-review', id] });
      await client.invalidateQueries({ queryKey: ['sense-reviews'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  }
  function edit() {
    if (!c) return;
    setMode('manual');
    setSaved(false);
    setDraft(
      c.proposal.patches.length
        ? c.proposal.patches.map((p) => ({ field: p.field, after: p.after }))
        : c.reason === 'missing'
          ? [
              {
                field: 'agregar',
                after: {
                  translation: '',
                  definition: '',
                  example: '',
                  example_es: '',
                  pos: 'noun',
                },
              },
            ]
          : c.reason === 'inappropriate'
            ? [{ field: 'quitar', after: null }]
            : [{ field: 'translation', after: '' }],
    );
  }
  if (q.isError) return <p role="alert">{q.error.message}</p>;
  if (!c) return <p role="status">Cargando caso…</p>;
  const locked = c.fixes.some((f) => ['publicada', 'revertida'].includes(f.state));
  return (
    <article className="card sense-detail">
      <h2 ref={heading} tabIndex={-1}>
        {c.word} · {reasons[c.reason]}
      </h2>
      {c.simulated && (
        <p className="badge b-warn">Banco aislado · sugerencia y revisión simuladas</p>
      )}
      <section>
        <h3>Qué avisó el alumno</h3>
        <p>
          {c.student === null
            ? 'El contexto privado fue retirado por retención.'
            : c.student.comment || 'Sin comentario adicional.'}
        </p>
        {c.student?.context && <blockquote>{c.student.context}</blockquote>}
      </section>
      <section>
        <h3>Cómo está la ficha hoy</h3>
        <TextValue value={c.current} />
        {c.stale && (
          <p role="alert">
            La ficha cambió desde el aviso. No se puede aceptar el contenido anterior.
          </p>
        )}
      </section>
      <section>
        <h3>Qué se propone y por qué</h3>
        <p>{actions[c.proposal.action]}</p>
        <p>{c.proposal.reason}</p>
        {c.reason === 'inappropriate' &&
          c.proposal.action !== 'retire' &&
          c.proposal.patches.length > 0 && (
            <p role="note">
              La sugerencia propone cambiar contenido. Aceptar este aviso como impropio significa
              retirar el significado completo para todos en la pasada. Se registrará este ajuste de
              acción.
            </p>
          )}
      </section>
      <section>
        <h3>Qué dijo la revisión</h3>
        <p>
          {c.proposal.available
            ? 'Coincide con la sugerencia.'
            : 'No dejó una aprobación válida; decidí a mano o rechazá.'}
        </p>
        <p>{c.proposal.review}</p>
      </section>
      {c.proposal.patches.map((p) => (
        <Compare key={p.field} patch={p} />
      ))}
      {c.proposal.action === 'keep' && (
        <div className="sense-columns">
          <div>
            <h3>Antes</h3>
            <TextValue value={c.observed} />
          </div>
          <div>
            <h3>Después</h3>
            <p>Se conserva este significado.</p>
            <TextValue value={c.observed} />
          </div>
        </div>
      )}
      {c.fixes.length > 0 && (
        <section>
          <h3>Lo que aceptaste para la pasada</h3>
          {c.fixes.map((f) => (
            <Compare key={f.field} patch={f} />
          ))}
        </section>
      )}
      {c.fixes.map((f) => (
        <p className="badge" key={f.field}>
          {fields[f.field]} · {states[f.state]}
          {f.publishedAt ? ` · ${new Date(f.publishedAt).toISOString()}` : ''}
        </p>
      ))}
      {c.decision && (
        <section>
          <h3>Tu última decisión</h3>
          <p>
            {
              { accept: 'Aceptaste', reject: 'Rechazaste', manual: 'Corregiste a mano' }[
                c.decision.action
              ]
            }{' '}
            · {c.decision.reason}
          </p>
          <time>{new Date(c.decision.at).toISOString()} · UTC</time>
        </section>
      )}
      {!locked && !['received', 'reviewing'].includes(c.status) && (
        <>
          <div className="sense-actions">
            <button
              className="btn btn-primary"
              onClick={() => void submit('accept')}
              disabled={busy || !!pending || c.stale || !c.proposal.available}
            >
              Aceptar
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setMode('reject');
                setSaved(false);
              }}
              disabled={busy || !!pending}
            >
              Rechazar
            </button>
            <button
              className="btn btn-secondary"
              onClick={edit}
              disabled={busy || !!pending || c.stale}
            >
              Corregir a mano
            </button>
          </div>
          {mode && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit(mode);
              }}
              className="sense-editor"
            >
              <h3>{mode === 'manual' ? 'Corrección a mano' : 'Rechazar sugerencia'}</h3>
              {mode === 'manual' && (
                <PatchEditor
                  caseData={c}
                  draft={draft}
                  setDraft={setDraft}
                  disabled={busy || !!pending}
                />
              )}
              <label>
                Motivo
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={600}
                  required
                  disabled={busy || !!pending}
                />
              </label>
              {c.proposal.available && (
                <>
                  <label>
                    Cómo evaluás la sugerencia
                    <select
                      value={assessment}
                      onChange={(e) => setAssessment(e.target.value as typeof assessment)}
                      disabled={busy || !!pending}
                    >
                      <option value="wrong">Se equivocó</option>
                      <option value="editorial">Ajusté la propuesta</option>
                    </select>
                  </label>
                  {assessment === 'wrong' && (
                    <label>
                      En qué se equivocó
                      <select
                        value={errorType}
                        onChange={(e) => setErrorType(e.target.value as typeof errorType)}
                        disabled={busy || !!pending}
                      >
                        {Object.entries(errorTypes).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </>
              )}
              <div className="sense-actions">
                <button className="btn btn-primary" type="submit" disabled={busy || !!pending}>
                  Guardar decisión
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={busy || !!pending}
                  onClick={() => setMode(null)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </>
      )}
      {error && (
        <div role="alert">
          <p>No se guardó la confirmación: {error}</p>
          {pending && (
            <button
              className="btn btn-secondary"
              onClick={() => void submit(pending.action)}
              disabled={busy}
            >
              Reintentar la misma decisión
            </button>
          )}
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => {
              setPending(null);
              void q.refetch();
            }}
          >
            Recargar y revisar
          </button>
        </div>
      )}
      {saved && (
        <p role="status">
          Decisión guardada. Las correcciones aceptadas esperan la pasada mensual.
        </p>
      )}
      <details>
        <summary>Detalle técnico e historial</summary>
        <p>
          {c.id} · {c.suggestionHash}
        </p>
        <pre>{JSON.stringify(c.technical, null, 2)}</pre>
      </details>
    </article>
  );
}
function PatchEditor({
  caseData,
  draft,
  setDraft,
  disabled,
}: {
  caseData: SenseReviewCase;
  draft: Array<{ field: SenseFixField; after: unknown }>;
  setDraft: (d: Array<{ field: SenseFixField; after: unknown }>) => void;
  disabled: boolean;
}) {
  function change(i: number, after: unknown) {
    setDraft(draft.map((p, j) => (j === i ? { ...p, after } : p)));
  }
  const simple: SenseFixField[] = ['translation', 'alts', 'definition', 'example', 'example_es'];
  return (
    <>
      {draft.map((p, i) => (
        <div key={i}>
          {caseData.reason === 'wrong' && (
            <label>
              Campo
              <select
                value={p.field}
                onChange={(e) =>
                  setDraft(
                    draft.map((p, j) =>
                      j === i
                        ? {
                            field: e.target.value as SenseFixField,
                            after: e.target.value === 'alts' ? [] : '',
                          }
                        : p,
                    ),
                  )
                }
                disabled={disabled}
              >
                {simple.map((f) => (
                  <option key={f} value={f}>
                    {fields[f]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {p.field === 'quitar' ? (
            <p>Se retirará este significado para todos en la pasada mensual.</p>
          ) : p.field === 'agregar' ? (
            Object.entries(p.after as Record<string, string>).map(([k, v]) => (
              <label key={k}>
                {fields[k] ?? 'Categoría gramatical'}
                {k === 'pos' ? (
                  <select
                    value={v}
                    onChange={(e) => change(i, { ...(p.after as object), [k]: e.target.value })}
                    disabled={disabled}
                  >
                    {[
                      'noun',
                      'verb',
                      'adjective',
                      'adverb',
                      'pronoun',
                      'preposition',
                      'conjunction',
                      'article',
                      'determiner',
                      'interjection',
                      'number',
                      'abbreviation',
                      'phrase',
                      'prefix',
                      'contraction',
                      'symbol',
                      'particle',
                    ].map((pos) => (
                      <option key={pos}>{pos}</option>
                    ))}
                  </select>
                ) : (
                  <textarea
                    value={v}
                    required
                    maxLength={k === 'translation' ? 100 : 500}
                    onChange={(e) => change(i, { ...(p.after as object), [k]: e.target.value })}
                    disabled={disabled}
                  />
                )}
              </label>
            ))
          ) : (
            <label>
              {fields[p.field]}
              <textarea
                required={p.field !== 'alts'}
                value={Array.isArray(p.after) ? p.after.join('\n') : String(p.after)}
                maxLength={p.field === 'translation' ? 100 : 500}
                onChange={(e) =>
                  change(
                    i,
                    p.field === 'alts'
                      ? e.target.value.split('\n').filter(Boolean)
                      : e.target.value,
                  )
                }
                disabled={disabled}
              />
            </label>
          )}
        </div>
      ))}
      {caseData.reason === 'wrong' && draft.length < 5 && (
        <button
          className="btn btn-secondary"
          type="button"
          disabled={disabled}
          onClick={() =>
            setDraft([
              ...draft,
              {
                field: simple.find((f) => !draft.some((p) => p.field === f)) ?? 'translation',
                after: '',
              },
            ])
          }
        >
          Agregar otro campo
        </button>
      )}
    </>
  );
}
